import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, Building2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BusinessInviteAccept() {
  const { token } = useParams();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState(null);
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/business/invite/${token}/preview`)
      .then(({ data }) => { setPreview(data); setName(data.name || ""); })
      .catch((e) => setErr(e.response?.data?.detail || "Invalid invite"));
  }, [token]);

  const accept = async (e) => {
    e?.preventDefault();
    try {
      setBusy(true);
      const payload = { token };
      if (!preview.user_exists) {
        if (!password || password.length < 8) return toast.error("Password must be at least 8 characters");
        payload.password = password; payload.name = name;
      }
      await axios.post(`${API}/business/invite/accept`, payload);
      if (refresh) await refresh();
      toast.success(`Welcome to ${preview.company_name}!`);
      nav("/business/dashboard");
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Failed to accept");
    } finally { setBusy(false); }
  };

  if (err) {
    return (
      <div className="mx-auto max-w-lg px-6 py-16 text-center" data-testid="biz-invite-error">
        <XCircle className="mx-auto h-12 w-12 text-rose-500" />
        <h1 className="mt-4 font-display text-2xl font-bold">Invite unavailable</h1>
        <p className="mt-2 text-slate-600">{err}</p>
        <Link to="/" className="mt-6 inline-block text-copper hover:underline">← Back to EasyStay</Link>
      </div>
    );
  }
  if (!preview) return <div className="mx-auto max-w-lg px-6 py-16 text-center text-slate-500"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;

  const emailMismatch = user && preview.user_exists && preview.email.toLowerCase() !== (user.email || "").toLowerCase();
  const needsLogin = preview.user_exists && !user;

  return (
    <div className="mx-auto max-w-lg px-6 py-12" data-testid="biz-invite-accept">
      <div className="rounded-2xl border border-slate-200 bg-white p-8 card-shadow">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-copper">
          <Building2 className="h-4 w-4" /> EasyStay Business invite
        </div>
        <h1 className="mt-2 font-display text-2xl font-bold text-slate-900">Join {preview.company_name}</h1>
        <p className="mt-2 text-sm text-slate-600">
          {preview.invited_by_email || "The company admin"} invited <b>{preview.email}</b> as {preview.role}.
        </p>
        {needsLogin ? (
          <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            An EasyStay account with <b>{preview.email}</b> already exists. Please sign in first, then reopen this invite link.
            <div className="mt-3">
              <Link to={`/login?next=${encodeURIComponent(window.location.pathname)}`} className="btn-primary text-sm">Sign in</Link>
            </div>
          </div>
        ) : emailMismatch ? (
          <div className="mt-6 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900" data-testid="biz-email-mismatch">
            You&apos;re signed in as <b>{user.email}</b> but this invite is for <b>{preview.email}</b>. Sign out first, then reopen this link.
          </div>
        ) : (
          <form onSubmit={accept} className="mt-6 space-y-3">
            {!preview.user_exists && (
              <>
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-500">Your name</label>
                  <input data-testid="biz-accept-name" value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Full name" className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-500">Choose a password</label>
                  <input data-testid="biz-accept-password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white" />
                </div>
              </>
            )}
            <button type="submit" data-testid="biz-accept-submit" disabled={busy} className="btn-primary flex w-full items-center justify-center gap-2 text-sm disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Join {preview.company_name}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
